import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import LoadingSpinner, { PageLoader } from '../components/LoadingSpinner';
import PreBidConfirmation from '../components/PreBidConfirmation';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/imageUtils';
import { 
  Gavel, Timer, Users, DollarSign, Trophy, Crown, Play, 
  ChevronUp, AlertCircle, CheckCircle, Video, Truck, X,
  Sparkles, Flame, Zap, MapPin, Package, CreditCard, ShieldAlert, Settings, Gift, PartyPopper
} from 'lucide-react';

// ==================== BID BUTTON COMPONENT ====================

const BidButton = ({ amount, currentPrice, onBid, disabled }) => {
  const newPrice = currentPrice + amount;
  return (
    <Button
      onClick={() => onBid(newPrice)}
      disabled={disabled}
      className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg"
    >
      <ChevronUp className="w-5 h-5 mr-1" />
      +${amount}
    </Button>
  );
};

// ==================== WINNER MODAL ====================

const WinnerModal = ({ auction, onCheckout, onClose }) => {
  const [address, setAddress] = useState({
    name: '',
    street1: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!address.name || !address.street1 || !address.city || !address.state || !address.zip) {
      toast.error('Please fill in all address fields');
      return;
    }
    setLoading(true);
    await onCheckout(address);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full overflow-hidden">
        {/* Celebration Header */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-400 p-6 text-center">
          <Trophy className="w-16 h-16 text-black mx-auto mb-2 animate-bounce" />
          <h2 className="text-2xl font-black text-black">CONGRATULATIONS!</h2>
          <p className="text-black/80 font-medium">YOU WON THE AUCTION!</p>
        </div>

        {/* Card Details */}
        <div className="p-6">
          <div className="flex gap-4 mb-6">
            {auction?.card_details?.image_url && (
              <img 
                src={resolveImageUrl(auction.card_details.image_url)}
                alt="Won card"
                className="w-24 h-32 object-contain rounded-lg bg-zinc-800"
              />
            )}
            <div>
              <h3 className="text-white font-bold text-lg">{auction?.card_details?.player_name}</h3>
              <p className="text-zinc-400">{auction?.card_details?.year} {auction?.card_details?.set_name}</p>
              <p className="text-emerald-400 font-bold text-2xl mt-2">${auction?.current_price?.toFixed(2)}</p>
            </div>
          </div>

          {/* Shipping Address Form */}
          <div className="space-y-3 mb-6">
            <h4 className="text-white font-semibold">Shipping Address</h4>
            <Input 
              placeholder="Full Name"
              value={address.name}
              onChange={(e) => setAddress({...address, name: e.target.value})}
              className="bg-zinc-800 border-zinc-700"
            />
            <Input 
              placeholder="Street Address"
              value={address.street1}
              onChange={(e) => setAddress({...address, street1: e.target.value})}
              className="bg-zinc-800 border-zinc-700"
            />
            <div className="grid grid-cols-3 gap-2">
              <Input 
                placeholder="City"
                value={address.city}
                onChange={(e) => setAddress({...address, city: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
              <Input 
                placeholder="State"
                value={address.state}
                onChange={(e) => setAddress({...address, state: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
              <Input 
                placeholder="ZIP"
                value={address.zip}
                onChange={(e) => setAddress({...address, zip: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          {/* Checkout Summary */}
          <div className="bg-zinc-800 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between text-zinc-400">
              <span>Winning Bid</span>
              <span className="text-white">${auction?.current_price?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>Shipping (USPS Ground)</span>
              <span className="text-white">~$5.20</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>Processing (3.5%)</span>
              <span className="text-white">${(auction?.current_price * 0.035)?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-zinc-700">
              <span className="text-white font-bold">Total</span>
              <span className="text-emerald-400 font-bold text-xl">
                ${((auction?.current_price || 0) + 5.20 + (auction?.current_price || 0) * 0.035).toFixed(2)}
              </span>
            </div>
          </div>

          <Button 
            onClick={handleCheckout}
            disabled={loading}
            className="w-full h-14 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg"
          >
            {loading ? <LoadingSpinner size="sm" /> : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Pay Now & Claim Your Card
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

function LiveAuctionPage() {
  const { auctionId } = useParams();
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [bidding, setBidding] = useState(false);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [recentBids, setRecentBids] = useState([]);
  
  // Pre-bid confirmation state
  const [showBidConfirmation, setShowBidConfirmation] = useState(false);
  const [hasConfirmedAddress, setHasConfirmedAddress] = useState(false);
  const [confirmedAddress, setConfirmedAddress] = useState(null);
  
  // Bid eligibility state
  const [bidEligibility, setBidEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  
  // Max bid (proxy) state
  const [maxBidInput, setMaxBidInput] = useState('');
  const [showMaxBidInput, setShowMaxBidInput] = useState(false);
  
  // Giveaway state
  const [hasEntered, setHasEntered] = useState(false);
  const [enteringGiveaway, setEnteringGiveaway] = useState(false);
  const [showWinnerReveal, setShowWinnerReveal] = useState(false);
  
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch bid eligibility when user is authenticated
  const checkBidEligibility = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setCheckingEligibility(true);
    try {
      const res = await api().get('/users/me/bid-eligibility');
      setBidEligibility(res.data);
      if (res.data.eligible && res.data.address_info) {
        setHasConfirmedAddress(true);
        setConfirmedAddress(res.data.address_info);
      }
    } catch (e) {
      console.error('Failed to check bid eligibility:', e);
    }
    setCheckingEligibility(false);
  }, [api, isAuthenticated]);

  // Fetch auction data
  const fetchAuction = useCallback(async () => {
    try {
      const res = await api().get(`/auctions/${auctionId}`);
      setAuction(res.data);
      setTimeRemaining(res.data.time_remaining_seconds);
      setRecentBids(res.data.recent_bids || []);
      
      // Check if user has entered giveaway
      if (res.data.is_giveaway && res.data.user_entries) {
        setHasEntered(res.data.user_entries.includes(user?.id));
      }
      
      // Check if user is the winner and auction ended
      if (res.data.status === 'ended' && res.data.highest_bidder_id === user?.id) {
        if (res.data.is_giveaway) {
          setShowWinnerReveal(true);
          // Show winner modal after reveal animation
          setTimeout(() => {
            setShowWinnerReveal(false);
            setShowWinnerModal(true);
          }, 3000);
        } else {
          setShowWinnerModal(true);
        }
      }
    } catch (e) {
      console.error('Fetch auction error:', e);
      toast.error('Failed to load auction');
    }
    setLoading(false);
  }, [auctionId, api, user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchAuction();
    checkBidEligibility();
  }, [fetchAuction, checkBidEligibility]);

  // Timer countdown
  useEffect(() => {
    if (auction?.status === 'live' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            fetchAuction(); // Refresh to get final state
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [auction?.status, fetchAuction]);

  // Poll for updates every 2 seconds
  useEffect(() => {
    if (auction?.status === 'live') {
      pollRef.current = setInterval(fetchAuction, 2000);
    }
    return () => clearInterval(pollRef.current);
  }, [auction?.status, fetchAuction]);

  // Handle bid attempt - check eligibility first
  const handleBidAttempt = (amount, maxBid = null) => {
    if (!isAuthenticated) {
      toast.error('Please login to bid');
      return;
    }
    
    // Check bid eligibility
    if (bidEligibility && !bidEligibility.eligible) {
      const issues = bidEligibility.issues || [];
      if (issues.length > 0) {
        toast.error(issues[0].message);
        return;
      }
    }
    
    // If user hasn't confirmed their address yet, show the confirmation modal
    if (!hasConfirmedAddress) {
      setShowBidConfirmation(true);
      // Store the intended bid amount for after confirmation
      sessionStorage.setItem('pendingBidAmount', amount.toString());
      if (maxBid) {
        sessionStorage.setItem('pendingMaxBid', maxBid.toString());
      }
      return;
    }
    
    // Address confirmed, place the bid
    placeBid(amount, maxBid);
  };

  // Handle address confirmation
  const handleAddressConfirmed = (address) => {
    setConfirmedAddress(address);
    setHasConfirmedAddress(true);
    setShowBidConfirmation(false);
    // Re-check eligibility after address confirmation
    checkBidEligibility();
    
    // If there was a pending bid, execute it
    const pendingBid = sessionStorage.getItem('pendingBidAmount');
    const pendingMaxBid = sessionStorage.getItem('pendingMaxBid');
    if (pendingBid) {
      sessionStorage.removeItem('pendingBidAmount');
      sessionStorage.removeItem('pendingMaxBid');
      setTimeout(() => placeBid(parseFloat(pendingBid), pendingMaxBid ? parseFloat(pendingMaxBid) : null), 500);
    }
  };

  // Place bid (supports both manual and proxy/max bids)
  const placeBid = async (amount, maxBid = null) => {
    if (!isAuthenticated) {
      toast.error('Please login to bid');
      return;
    }
    
    setBidding(true);
    try {
      const payload = { amount };
      if (maxBid) {
        payload.max_bid = maxBid;
      }
      
      const res = await api().post(`/auctions/${auctionId}/bid`, payload);
      setAuction(prev => ({
        ...prev,
        current_price: res.data.current_price,
        highest_bidder_id: user?.id,
        highest_bidder_name: user?.name,
        bid_count: (prev?.bid_count || 0) + 1
      }));
      setTimeRemaining(res.data.time_remaining_seconds);
      
      if (res.data.extension_triggered) {
        toast.success('Extension! +5 seconds added!');
      } else if (res.data.is_proxy_bid) {
        toast.success(res.data.message);
      } else {
        toast.success(`Bid placed: $${res.data.current_price?.toFixed(2)}`);
      }
      
      // Clear max bid input after successful proxy bid
      if (maxBid) {
        setMaxBidInput('');
        setShowMaxBidInput(false);
      }
      
      fetchAuction();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to place bid';
      // If error is about missing address, show confirmation
      if (errorMsg.includes('shipping address')) {
        setHasConfirmedAddress(false);
        setShowBidConfirmation(true);
        checkBidEligibility();
      } else if (errorMsg.includes('payment method')) {
        // Refresh eligibility to show payment method warning
        checkBidEligibility();
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
    }
    setBidding(false);
  };

  // Handle max bid submission
  const handleMaxBidSubmit = () => {
    const maxBidValue = parseFloat(maxBidInput);
    if (isNaN(maxBidValue) || maxBidValue <= 0) {
      toast.error('Please enter a valid max bid amount');
      return;
    }
    
    const minBid = auction.current_price + (auction.bid_increment || 1);
    if (maxBidValue < minBid) {
      toast.error(`Max bid must be at least $${minBid.toFixed(2)}`);
      return;
    }
    
    handleBidAttempt(minBid, maxBidValue);
  };

  // Handle giveaway entry
  const handleEnterGiveaway = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to enter the giveaway');
      return;
    }
    
    // Check eligibility (need verified address)
    if (bidEligibility && !bidEligibility.has_verified_address) {
      toast.error('You need a verified shipping address to enter giveaways. Go to Settings.');
      return;
    }
    
    setEnteringGiveaway(true);
    try {
      const res = await api().post(`/auctions/${auctionId}/enter-giveaway`);
      toast.success(res.data.message || "You're in! Good luck! 🍀");
      setHasEntered(true);
      fetchAuction();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to enter giveaway';
      if (errorMsg.includes('shipping address')) {
        checkBidEligibility();
      }
      toast.error(errorMsg);
    }
    setEnteringGiveaway(false);
  };



  // Winner checkout
  const handleCheckout = async (shippingAddress) => {
    try {
      const res = await api().post(`/auctions/${auctionId}/checkout`, shippingAddress);
      toast.success('Order created! Redirecting to payment...');
      // Navigate to payment page or handle Stripe checkout
      navigate(`/checkout/${res.data.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer color based on urgency
  const getTimerColor = () => {
    if (timeRemaining <= 10) return 'text-red-500 animate-pulse';
    if (timeRemaining <= 30) return 'text-amber-500';
    return 'text-white';
  };

  if (loading) return <PageLoader />;

  if (!auction) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <div className="pt-24 px-4 text-center">
          <p className="text-zinc-400">Auction not found</p>
          <Link to="/auctions"><Button className="mt-4">Browse Auctions</Button></Link>
        </div>
      </div>
    );
  }

  const isHost = user?.id === auction.host_id;
  const isWinner = auction.highest_bidder_id === user?.id;
  const isEnded = auction.status === 'ended';
  const bidIncrement = auction.bid_increment || 1;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      {/* Winner Modal */}
      {showWinnerModal && isWinner && (
        <WinnerModal 
          auction={auction} 
          onCheckout={handleCheckout}
          onClose={() => setShowWinnerModal(false)}
        />
      )}

      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Main Video/Card Area */}
            <div className="lg:col-span-2 space-y-4">
              {/* Stream/Card Display */}
              <div className="relative aspect-video bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800">
                {auction.stream_url ? (
                  <iframe 
                    src={auction.stream_url}
                    className="w-full h-full"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  />
                ) : auction.card_details?.image_url ? (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                    <img 
                      src={resolveImageUrl(auction.card_details.image_url)}
                      alt={auction.card_details.player_name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-16 h-16 text-zinc-700" />
                  </div>
                )}

                {/* LIVE Badge */}
                {auction.status === 'live' && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white font-bold text-sm">LIVE</span>
                  </div>
                )}

                {/* Shipping Cap Badge - Display on Stream Overlay */}
                {auction.max_shipping_fee && (
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-amber-500/90 rounded-full">
                    <span className="text-black font-bold text-sm flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      ${auction.max_shipping_fee.toFixed(2)} Shipping Cap
                    </span>
                  </div>
                )}

                {/* Current Bid Overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-black/80 backdrop-blur-sm rounded-xl p-4 border border-zinc-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-zinc-400 text-sm">Current Bid</p>
                        <p className="text-3xl font-black text-emerald-400">
                          ${auction.current_price?.toFixed(2)}
                        </p>
                        {auction.highest_bidder_name && (
                          <p className="text-zinc-500 text-sm">
                            by {isWinner ? 'You' : auction.highest_bidder_name}
                          </p>
                        )}
                      </div>
                      
                      {/* Timer */}
                      <div className="text-right">
                        <p className="text-zinc-400 text-sm flex items-center gap-1">
                          <Timer className="w-4 h-4" />
                          {isEnded ? 'Ended' : 'Time Left'}
                        </p>
                        <p className={`text-4xl font-mono font-black ${getTimerColor()}`}>
                          {isEnded ? '0:00' : formatTime(timeRemaining)}
                        </p>
                        {timeRemaining <= 10 && !isEnded && (
                          <p className="text-red-400 text-xs flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            Sudden Death!
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Info */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h1 className="text-xl font-bold text-white">
                  {auction.card_details?.player_name}
                </h1>
                <p className="text-zinc-400">
                  {auction.card_details?.year} {auction.card_details?.set_name}
                  {auction.card_details?.grade_company && ` • ${auction.card_details.grade_company} ${auction.card_details.grade_value || ''}`}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
                  <span className="flex items-center gap-1">
                    {auction.is_giveaway ? <Gift className="w-4 h-4" /> : <Gavel className="w-4 h-4" />}
                    {auction.is_giveaway ? `${auction.entry_count || auction.bid_count} entries` : `${auction.bid_count} bids`}
                  </span>
                  <span>Hosted by {auction.host_name}</span>
                </div>
                {auction.is_giveaway && (
                  <div className="mt-2 px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded-md inline-flex items-center gap-1 text-purple-400 text-xs">
                    <Gift className="w-3 h-3" />
                    GIVEAWAY - Random Winner
                  </div>
                )}
              </div>
            </div>

            {/* Bidding Panel */}
            <div className="space-y-4">
              {/* Bid Controls */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  {auction.is_giveaway ? (
                    <>
                      <Gift className="w-5 h-5 text-purple-400" />
                      Join Giveaway
                    </>
                  ) : (
                    <>
                      <Gavel className="w-5 h-5 text-[#00BFFF]" />
                      Place Your Bid
                    </>
                  )}
                </h2>

                {isEnded ? (
                  <div className="text-center py-6">
                    {auction.is_giveaway ? (
                      <>
                        <PartyPopper className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                        <p className="text-white font-bold">Giveaway Ended</p>
                        {auction.highest_bidder_name ? (
                          <p className="text-zinc-400">
                            Winner: {isWinner ? '🎉 YOU!' : auction.highest_bidder_name}
                          </p>
                        ) : (
                          <p className="text-zinc-400">No entries</p>
                        )}
                      </>
                    ) : (
                      <>
                        <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                        <p className="text-white font-bold">Auction Ended</p>
                        {auction.highest_bidder_name ? (
                          <p className="text-zinc-400">
                            Won by {isWinner ? 'You!' : auction.highest_bidder_name} for ${auction.current_price?.toFixed(2)}
                          </p>
                        ) : (
                          <p className="text-zinc-400">No bids placed</p>
                        )}
                      </>
                    )}
                    {isWinner && !showWinnerModal && (
                      <Button 
                        onClick={() => setShowWinnerModal(true)}
                        className="mt-4 bg-emerald-500 text-black"
                      >
                        Complete Purchase
                      </Button>
                    )}
                  </div>
                ) : isHost ? (
                  <div className="text-center py-6">
                    <Crown className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <p className="text-white font-bold">You're the Host</p>
                    <p className="text-zinc-400 text-sm">You cannot bid on your own auction</p>
                  </div>
                ) : (
                  <>
                    {/* Eligibility Warnings */}
                    {isAuthenticated && bidEligibility && !bidEligibility.eligible && (
                      <div className="mb-4 space-y-2">
                        {bidEligibility.issues?.map((issue, idx) => (
                          <div key={idx} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <div className="flex items-start gap-2">
                              {issue.type === 'no_address' || issue.type === 'unverified_address' ? (
                                <MapPin className="w-4 h-4 text-red-400 mt-0.5" />
                              ) : (
                                <CreditCard className="w-4 h-4 text-red-400 mt-0.5" />
                              )}
                              <div>
                                <p className="text-red-400 text-sm font-medium">{issue.message}</p>
                                <p className="text-zinc-500 text-xs mt-1">{issue.action}</p>
                              </div>
                            </div>
                            <Link to="/settings" className="mt-2 inline-flex items-center gap-1 text-xs text-[#00BFFF] hover:underline">
                              <Settings className="w-3 h-3" />
                              Go to Settings
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Confirmed Address Display */}
                    {hasConfirmedAddress && confirmedAddress && (
                      <div className="p-3 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-lg mb-4">
                        <p className="text-[#00BFFF] text-xs font-medium flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          Shipping to: {confirmedAddress.city}, {confirmedAddress.state} {confirmedAddress.zip}
                        </p>
                      </div>
                    )}

                    {/* Payment Method Status */}
                    {bidEligibility?.has_payment_method && bidEligibility?.payment_info && (
                      <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                        <p className="text-emerald-400 text-xs font-medium flex items-center gap-2">
                          <CreditCard className="w-3 h-3" />
                          {bidEligibility.payment_info.brand} •••• {bidEligibility.payment_info.last4}
                        </p>
                      </div>
                    )}

                    {/* Timer Mode Indicator */}
                    {auction.timer_mode && !auction.is_giveaway && (
                      <div className={`p-2 rounded-lg mb-4 flex items-center gap-2 text-xs font-medium ${
                        auction.timer_mode === 'sudden_death' 
                          ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                          : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                      }`}>
                        {auction.timer_mode === 'sudden_death' ? (
                          <>
                            <Flame className="w-3 h-3" />
                            Sudden Death - Timer ends exactly at 0
                          </>
                        ) : (
                          <>
                            <Zap className="w-3 h-3" />
                            Extension Auction - +5s on late bids
                          </>
                        )}
                      </div>
                    )}

                    {/* GIVEAWAY MODE - Join Button */}
                    {auction.is_giveaway ? (
                      <div className="space-y-4">
                        {/* Entry Count */}
                        <div className="text-center p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <p className="text-purple-400 text-3xl font-bold">{auction.entry_count || auction.bid_count}</p>
                          <p className="text-zinc-400 text-sm">entries</p>
                        </div>

                        {hasEntered ? (
                          <div className="text-center py-4">
                            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                            <p className="text-emerald-400 font-bold text-lg">You're In!</p>
                            <p className="text-zinc-400 text-sm">Good luck! Winner announced at 0:00</p>
                          </div>
                        ) : (
                          <Button
                            onClick={handleEnterGiveaway}
                            disabled={enteringGiveaway || (bidEligibility && !bidEligibility.has_verified_address)}
                            className="w-full h-16 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-bold text-xl"
                          >
                            {enteringGiveaway ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <>
                                <Gift className="w-6 h-6 mr-2" />
                                Join Giveaway FREE
                              </>
                            )}
                          </Button>
                        )}

                        <p className="text-zinc-500 text-xs text-center">
                          🎲 Random winner selected when timer hits 0:00
                        </p>
                      </div>
                    ) : (
                      <>
                    {/* Quick Bid Buttons */}
                    <div className="space-y-3 mb-4">
                      <p className="text-zinc-400 text-sm">
                        Next minimum bid: <span className="text-white font-bold">${(auction.current_price + bidIncrement).toFixed(2)}</span>
                      </p>
                      
                      <div className="flex gap-2">
                        <BidButton 
                          amount={1} 
                          currentPrice={auction.current_price} 
                          onBid={handleBidAttempt}
                          disabled={bidding || (bidEligibility && !bidEligibility.eligible)}
                        />
                        <BidButton 
                          amount={5} 
                          currentPrice={auction.current_price} 
                          onBid={handleBidAttempt}
                          disabled={bidding || (bidEligibility && !bidEligibility.eligible)}
                        />
                        <BidButton 
                          amount={10} 
                          currentPrice={auction.current_price} 
                          onBid={handleBidAttempt}
                          disabled={bidding || (bidEligibility && !bidEligibility.eligible)}
                        />
                      </div>
                    </div>

                    {/* Max Bid (Proxy Bidding) Section */}
                    <div className="border-t border-zinc-800 pt-4 mb-4">
                      <button
                        onClick={() => setShowMaxBidInput(!showMaxBidInput)}
                        className="text-[#00BFFF] text-sm font-medium hover:underline flex items-center gap-1"
                      >
                        <Sparkles className="w-4 h-4" />
                        {showMaxBidInput ? 'Hide' : 'Set'} Max Bid (Auto-Bid)
                      </button>
                      
                      {showMaxBidInput && (
                        <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-zinc-400 text-xs mb-2">
                            Set a maximum bid and the system will automatically bid for you up to that amount.
                          </p>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min={auction.current_price + bidIncrement}
                                value={maxBidInput}
                                onChange={(e) => setMaxBidInput(e.target.value)}
                                placeholder={`Min: ${(auction.current_price + bidIncrement).toFixed(2)}`}
                                className="bg-zinc-900 border-zinc-700 pl-7"
                              />
                            </div>
                            <Button
                              onClick={handleMaxBidSubmit}
                              disabled={bidding || !maxBidInput || (bidEligibility && !bidEligibility.eligible)}
                              className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-bold"
                            >
                              {bidding ? <LoadingSpinner size="sm" /> : 'Set Max'}
                            </Button>
                          </div>
                          <p className="text-zinc-500 text-xs mt-2">
                            Your max is private. Others only see the current bid.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Winning Indicator */}
                    {isWinner && (
                      <div className="p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg mb-4">
                        <p className="text-emerald-400 font-bold flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          You're Winning!
                        </p>
                      </div>
                    )}

                    {!isAuthenticated && (
                      <div className="p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg">
                        <p className="text-amber-400 text-sm">
                          <Link to="/login" className="underline font-bold">Login</Link> to place bids
                        </p>
                      </div>
                    )}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Recent Bids */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
                  Bid History
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recentBids.length > 0 ? (
                    [...recentBids].reverse().map((bid, idx) => (
                      <div 
                        key={bid.id || idx}
                        className={`flex justify-between items-center p-2 rounded ${
                          idx === 0 ? 'bg-emerald-500/20' : 'bg-zinc-800'
                        }`}
                      >
                        <span className="text-zinc-300 text-sm">{bid.bidder_name}</span>
                        <span className="text-white font-bold">${bid.amount?.toFixed(2)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-500 text-center py-4">No bids yet. Be the first!</p>
                  )}
                </div>
              </div>

              {/* Shipping Info */}
              <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-2 text-sm text-zinc-400">
                <Truck className="w-4 h-4" />
                <span>Shipping: USPS Ground Advantage (~$5.20)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pre-Bid Confirmation Modal */}
      {showBidConfirmation && (
        <PreBidConfirmation
          auctionId={auctionId}
          onConfirm={handleAddressConfirmed}
          onCancel={() => {
            setShowBidConfirmation(false);
            sessionStorage.removeItem('pendingBidAmount');
            sessionStorage.removeItem('pendingMaxBid');
          }}
        />
      )}

      {/* Winner Reveal Animation (Giveaways) */}
      {showWinnerReveal && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center">
          <div className="text-center animate-pulse">
            <PartyPopper className="w-24 h-24 text-purple-400 mx-auto mb-4 animate-bounce" />
            <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4">
              🎉 YOU WON! 🎉
            </h1>
            <p className="text-2xl text-white font-bold">{auction?.card_details?.player_name}</p>
            <p className="text-zinc-400 mt-2">Preparing your FREE item...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveAuctionPage;
